import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  AccountTree as SopIcon,
  OpenInNew as DetailsIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  WarningAmber as RiskIcon,
  Nightlight as DormantIcon,
  BuildCircle as SetupIcon,
  RocketLaunch as DemoReadyIcon,
  TrendingUp as DeepIcon,
  CheckCircle as OperatingIcon,
  School as SchoolIcon,
  Today as DailyIcon,
  Login as LoginIcon,
  Payments as FeeIcon,
  FactCheck as AttendanceIcon,
  Campaign as NoticeIcon,
  HowToReg as StaffAttIcon,
  MenuBook as HomeworkIcon,
  AccountBalanceWallet as ExpenseIcon,
  Apps as ModuleIcon,
  MonitorHeart as SystemIcon,
  Explore as ExploreActivityIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

const BANDS = [
  "ALL",
  "At risk",
  "Dormant",
  "Setup gap",
  "Demo ready",
  "Operating",
  "Deep use",
];

const BAND_COLOR = {
  "At risk": crmColors.error.main,
  Dormant: crmColors.secondary.main,
  "Setup gap": crmColors.warning.main,
  "Demo ready": crmColors.info?.main || "#0288D1",
  Operating: crmColors.primary.main,
  "Deep use": crmColors.success.main,
  Unknown: crmColors.secondary.light,
};

//Created By Cursor — Daily Activity period presets
const DAILY_RANGES = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_10_days", label: "Last 10 Days" },
  { value: "last_15_days", label: "Last 15 Days" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "last_30_days", label: "Last 30 Days" },
];

//Created By Cursor — page size options for daily school list
const DAILY_PAGE_SIZES = [50, 100, 200, 500, 1000, 1500, 2000];

//Created By Cursor — filter list by activity type
const DAILY_ACTIVITIES = [
  { value: "all", label: "All activities" },
  { value: "login", label: "Staff login" },
  { value: "fee", label: "Fee collection" },
  { value: "attendance", label: "Student attendance" },
  { value: "staff_attendance", label: "Staff attendance" },
  { value: "homework", label: "Homework" },
  { value: "expense", label: "Expense" },
  { value: "notice", label: "Notice" },
];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

//Created By Cursor
const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

//Created By Cursor
const SummaryTile = ({
  label,
  value,
  color,
  icon,
  active,
  onClick,
  showExplore = false,
  onExplore,
}) => (
  <Box
    onClick={onClick}
    sx={{
      p: 1.25,
      height: "100%",
      borderRadius: "12px",
      cursor: onClick ? "pointer" : "default",
      border: `1px solid ${alpha(color, active ? 0.5 : 0.22)}`,
      background: `linear-gradient(145deg, ${alpha(color, active ? 0.16 : 0.08)} 0%, #fff 72%)`,
      display: "flex",
      flexDirection: "column",
      gap: 0.85,
      transition: "transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease",
      "&:hover": onClick
        ? {
            transform: "translateY(-1px)",
            borderColor: alpha(color, 0.45),
            boxShadow: `0 8px 18px ${alpha(color, 0.12)}`,
          }
        : undefined,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "8px",
          display: "grid",
          placeItems: "center",
          bgcolor: alpha(color, 0.14),
          color,
          flexShrink: 0,
          "& .MuiSvgIcon-root": { fontSize: 14 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: crmColors.text.secondary,
            lineHeight: 1.15,
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: 17, color: crmColors.text.primary, lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
    </Box>
    {showExplore ? (
      <Button
        size="small"
        variant="contained"
        disableElevation
        startIcon={<DetailsIcon sx={{ fontSize: "12px !important" }} />}
        onClick={(e) => {
          e.stopPropagation();
          if (onExplore) onExplore(e);
          else onClick?.(e);
        }}
        sx={{
          alignSelf: "stretch",
          textTransform: "none",
          fontWeight: 800,
          fontSize: 10.5,
          height: 24,
          minWidth: 0,
          px: 1,
          borderRadius: "7px",
          bgcolor: color,
          color: "#fff",
          boxShadow: "none",
          "&:hover": { bgcolor: color, filter: "brightness(0.92)", boxShadow: "none" },
          "& .MuiButton-startIcon": { mr: 0.4 },
        }}
      >
        Explore
      </Button>
    ) : null}
  </Box>
);

//Created By Cursor
const ScoreBar = ({ label, value, max = 40, color }) => (
  <Box sx={{ minWidth: 0, flex: 1 }}>
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.3 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: crmColors.text.secondary }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 10.5, fontWeight: 800 }}>{value}/{max}</Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={Math.min(100, (Number(value) / max) * 100)}
      sx={{
        height: 6,
        borderRadius: 99,
        bgcolor: alpha(color, 0.12),
        "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 99 },
      }}
    />
  </Box>
);

const SOP = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("daily"); // daily | modules | health
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [band, setBand] = useState("ALL");
  const [stage, setStage] = useState("ALL");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [dailyRows, setDailyRows] = useState([]);
  const [dailyTotal, setDailyTotal] = useState(0);
  const [dailySummary, setDailySummary] = useState(null);
  const [dailyDate, setDailyDate] = useState("");
  const [dailyDateFrom, setDailyDateFrom] = useState("");
  const [dailyDateTo, setDailyDateTo] = useState("");
  const [dailyRange, setDailyRange] = useState("today");
  const [dailyRangeLabel, setDailyRangeLabel] = useState("Today");
  const [dailyActivity, setDailyActivity] = useState("all");
  const [dailyPage, setDailyPage] = useState(1);
  const [dailyPageSize, setDailyPageSize] = useState(100);
  const [dailyLoading, setDailyLoading] = useState(false);

  const firstLoadRef = useRef(true);

  const stageOptions = useMemo(() => {
    const labels = summary?.stage_labels || [];
    if (labels.length) return labels;
    return [
      { stage: "1", label: "Account live" },
      { stage: "2", label: "Academic setup" },
      { stage: "3", label: "Fee setup" },
      { stage: "4", label: "First operating week" },
      { stage: "5", label: "Parent habit" },
      { stage: "6", label: "Exam cycle" },
      { stage: "7", label: "Save and expand" },
    ];
  }, [summary]);

  //Created By Cursor — auto-refresh only on first open; filters read cached health
  const loadList = useCallback(
    async ({ autoRefresh } = {}) => {
      setLoading(true);
      try {
        const ar =
          autoRefresh != null ? autoRefresh : firstLoadRef.current ? 1 : 0;
        const res = await apiService.getSopSchoolList({
          health_band: band,
          sop_stage: stage,
          q: query,
          limit: 300,
          offset: 0,
          auto_refresh: ar,
        });
        firstLoadRef.current = false;
        if (res.success) {
          setRows(Array.isArray(res.data) ? res.data : []);
          setTotal(res.total || 0);
          setSummary(res.summary || null);
        } else {
          toast.error(res.error || res.message || "Failed to load SOP list");
        }
      } catch (e) {
        toast.error(e?.message || "Failed to load SOP list");
      } finally {
        setLoading(false);
      }
    },
    [band, stage, query]
  );

  //Created By Cursor — login / fee / attendance pulse for selected range + page
  const loadDaily = useCallback(async () => {
    setDailyLoading(true);
    try {
      const activityFilter = viewMode === "modules" ? "all" : dailyActivity;
      const res = await apiService.getSopDailyActivity({
        q: query,
        range: dailyRange,
        activity: activityFilter,
        limit: dailyPageSize,
        offset: (dailyPage - 1) * dailyPageSize,
      });
      if (res.success) {
        setDailyRows(Array.isArray(res.data) ? res.data : []);
        setDailyTotal(res.total || 0);
        setDailySummary(res.summary || null);
        setDailyDate(res.date || "");
        setDailyDateFrom(res.date_from || "");
        setDailyDateTo(res.date_to || "");
        setDailyRangeLabel(res.range_label || dailyRange);
        const maxPage = Math.max(1, Math.ceil((res.total || 0) / dailyPageSize));
        if (dailyPage > maxPage) {
          setDailyPage(maxPage);
        }
      } else {
        toast.error(res.error || res.message || "Failed to load daily activity");
        setDailyRows([]);
        setDailyTotal(0);
        setDailySummary(null);
      }
    } catch (e) {
      toast.error(e?.message || "Failed to load daily activity");
    } finally {
      setDailyLoading(false);
    }
  }, [query, dailyRange, dailyActivity, dailyPage, dailyPageSize, viewMode]);

  useEffect(() => {
    if (viewMode === "health") {
      loadList();
    }
  }, [loadList, viewMode]);

  useEffect(() => {
    if (viewMode === "daily" || viewMode === "modules") {
      loadDaily();
    }
  }, [loadDaily, viewMode]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDailyPage(1);
      setQuery(search.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  //Created By Cursor
  const onRefreshBatch = async () => {
    if (viewMode === "daily" || viewMode === "modules") {
      await loadDaily();
      toast.success(
        viewMode === "modules" ? "Module usage refreshed" : "Daily activity refreshed"
      );
      return;
    }
    setRefreshing(true);
    try {
      const res = await apiService.refreshSchoolSopHealth({ limit: 40, stale_hours: 1 });
      if (res.success) {
        toast.success(res.message || `Refreshed ${res.data?.refreshed || 0} schools`);
        await loadList({ autoRefresh: 0 });
      } else {
        toast.error(res.error || "Refresh failed");
      }
    } finally {
      setRefreshing(false);
    }
  };

  //Created By Cursor — open school SOP details page
  const openSopDetails = (rowOrCode) => {
    const code =
      typeof rowOrCode === "string" ? rowOrCode : rowOrCode?.school_code;
    if (!code) return;
    navigate(`/sop/school/${encodeURIComponent(code)}`);
  };

  const bands = summary?.bands || {};
  const ds = dailySummary || {};
  const isActivityView = viewMode === "daily" || viewMode === "modules";
  const pageLoading = isActivityView ? dailyLoading : loading;
  const pageTotal = isActivityView ? dailyTotal : total;
  const dailyPageCount = Math.max(1, Math.ceil(dailyTotal / dailyPageSize) || 1);
  const dailyFrom = dailyTotal === 0 ? 0 : (dailyPage - 1) * dailyPageSize + 1;
  const dailyTo = Math.min(dailyPage * dailyPageSize, dailyTotal);

  const formatTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  const actionBtnSx = {
    textTransform: "none",
    borderRadius: "10px",
    fontWeight: 800,
    px: 1.75,
    background: crmColors.energy.main,
    backgroundImage: "none",
    color: "#fff",
    border: `1px solid ${alpha("#fff", 0.55)}`,
    boxShadow: `0 4px 12px ${alpha(crmColors.energy.main, 0.4)}`,
    "&:hover": {
      background: crmColors.energy.dark,
      backgroundImage: "none",
    },
    "&.Mui-disabled": {
      bgcolor: alpha(crmColors.energy.main, 0.4),
      color: "#fff",
    },
  };

  //Created By Cursor — open School SOP details from list rows
  const exploreBtnSx = {
    textTransform: "none",
    fontWeight: 800,
    fontSize: 11.5,
    height: 30,
    minWidth: 88,
    px: 1.25,
    borderRadius: "8px",
    bgcolor: crmColors.primary.main,
    color: "#fff",
    boxShadow: "none",
    "&:hover": { bgcolor: crmColors.primary.dark, boxShadow: "none" },
  };

  const goExploreSchool = (e, schoolCode) => {
    e?.stopPropagation?.();
    openSopDetails(schoolCode);
  };

  //Created By Cursor — activity KPI / filter; Module Usage jumps to Daily Activity
  const selectDailyActivity = (next) => {
    setDailyPage(1);
    setDailyActivity(next);
    if (viewMode === "modules") {
      setViewMode("daily");
    }
  };

  return (
    <PageContentLayout
      title="Standard Operating Procedure"
      subtitle={
        isActivityView
          ? `SOP · ${viewMode === "modules" ? "Module Usage" : "Daily Activity"} · ${dailyRangeLabel}${
              dailyDateFrom && dailyDateTo
                ? dailyDateFrom === dailyDateTo
                  ? ` · ${formatDate(dailyDateFrom)}`
                  : ` · ${formatDate(dailyDateFrom)} – ${formatDate(dailyDateTo)}`
                : dailyDate
                  ? ` · ${formatDate(dailyDate)}`
                  : ""
            }`
          : "SOP · System health · setup completeness, usability & service stage"
      }
      barSubtitle={
        isActivityView
          ? `${pageTotal} with activity · ${dailyRangeLabel}`
          : `${pageTotal} school${pageTotal === 1 ? "" : "s"}`
      }
      breadcrumbs={[
        {
          label: "Standard Operating Procedure",
          icon: <SopIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
      ]}
      backTo="/dashboard"
      darkBar
      actions={
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={
            refreshing || dailyLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <RefreshIcon sx={{ fontSize: 18 }} />
            )
          }
          disabled={refreshing || pageLoading}
          onClick={onRefreshBatch}
          sx={actionBtnSx}
        >
          {isActivityView ? "Refresh activity" : "Refresh scores"}
        </Button>
      }
    >
      <Stack spacing={1.75}>
        <Box
          sx={{
            borderRadius: "12px",
            border: `1px solid ${crmColors.border.default}`,
            bgcolor: "#fff",
            px: 1.25,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: crmColors.text.secondary,
            }}
          >
            View
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={(_e, next) => {
              if (!next) return;
              if (next === "explore") {
                navigate(
                  `/sop/explore-activity?activity=${
                    dailyActivity === "all" ? "login" : dailyActivity
                  }&range=${dailyRange}`
                );
                return;
              }
              if (next === "modules") {
                navigate(`/sop/module-usage?range=${dailyRange}`);
                return;
              }
              setViewMode(next);
            }}
            sx={{
              bgcolor: alpha(crmColors.primary.main, 0.04),
              borderRadius: "9px",
              p: 0.25,
              "& .MuiToggleButtonGroup-grouped": {
                border: 0,
                borderRadius: "7px !important",
                px: 1.35,
                py: 0.4,
                textTransform: "none",
                fontWeight: 800,
                fontSize: 12.5,
                color: crmColors.text.secondary,
                gap: 0.45,
                "&.Mui-selected": {
                  bgcolor: crmColors.primary.main,
                  color: "#fff",
                  boxShadow: `0 2px 8px ${alpha(crmColors.primary.main, 0.28)}`,
                  "&:hover": { bgcolor: crmColors.primary.dark },
                },
              },
            }}
          >
            <ToggleButton value="daily">
              <DailyIcon sx={{ fontSize: 15 }} />
              Daily Activity
            </ToggleButton>
            <ToggleButton value="explore">
              <ExploreActivityIcon sx={{ fontSize: 15 }} />
              Explore Activity
            </ToggleButton>
            <ToggleButton value="modules">
              <ModuleIcon sx={{ fontSize: 15 }} />
              Module Usage
            </ToggleButton>
            <ToggleButton value="health">
              <SystemIcon sx={{ fontSize: 15 }} />
              System health
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {viewMode === "health" ? (
          <Grid container spacing={1.25}>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryTile
                label="At risk"
                value={bands["At risk"] || 0}
                color={BAND_COLOR["At risk"]}
                icon={<RiskIcon sx={{ fontSize: 20 }} />}
                active={band === "At risk"}
                showExplore
                onClick={() => setBand(band === "At risk" ? "ALL" : "At risk")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryTile
                label="Dormant"
                value={bands.Dormant || 0}
                color={BAND_COLOR.Dormant}
                icon={<DormantIcon sx={{ fontSize: 20 }} />}
                active={band === "Dormant"}
                showExplore
                onClick={() => setBand(band === "Dormant" ? "ALL" : "Dormant")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryTile
                label="Setup gap"
                value={bands["Setup gap"] || 0}
                color={BAND_COLOR["Setup gap"]}
                icon={<SetupIcon sx={{ fontSize: 20 }} />}
                active={band === "Setup gap"}
                showExplore
                onClick={() => setBand(band === "Setup gap" ? "ALL" : "Setup gap")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryTile
                label="Demo ready"
                value={bands["Demo ready"] || 0}
                color={BAND_COLOR["Demo ready"]}
                icon={<DemoReadyIcon sx={{ fontSize: 20 }} />}
                active={band === "Demo ready"}
                showExplore
                onClick={() => setBand(band === "Demo ready" ? "ALL" : "Demo ready")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryTile
                label="Operating"
                value={bands.Operating || 0}
                color={BAND_COLOR.Operating}
                icon={<OperatingIcon sx={{ fontSize: 20 }} />}
                active={band === "Operating"}
                showExplore
                onClick={() => setBand(band === "Operating" ? "ALL" : "Operating")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryTile
                label="Deep use"
                value={bands["Deep use"] || 0}
                color={BAND_COLOR["Deep use"]}
                icon={<DeepIcon sx={{ fontSize: 20 }} />}
                active={band === "Deep use"}
                showExplore
                onClick={() => setBand(band === "Deep use" ? "ALL" : "Deep use")}
              />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={1.25}>
            <Grid item xs={6} sm={4} md={1.5}>
              <SummaryTile
                label="Total active"
                value={ds.total_schools_active || 0}
                color={crmColors.primary.main}
                icon={<SchoolIcon sx={{ fontSize: 20 }} />}
                active={viewMode === "modules" || dailyActivity === "all"}
                showExplore
                onClick={() => selectDailyActivity("all")}
                onExplore={() =>
                  navigate(`/sop/explore-activity?activity=login&range=${dailyRange}`)
                }
              />
            </Grid>
            <Grid item xs={6} sm={4} md={1.5}>
              <SummaryTile
                label={dailyRange === "today" ? "Active today" : "With activity"}
                value={ds.schools_active || dailyTotal || 0}
                color={crmColors.success.main}
                icon={<DailyIcon sx={{ fontSize: 20 }} />}
                active={viewMode === "modules" || dailyActivity === "all"}
                showExplore
                onClick={() => selectDailyActivity("all")}
                onExplore={() =>
                  navigate(`/sop/explore-activity?activity=login&range=${dailyRange}`)
                }
              />
            </Grid>
            <Grid item xs={6} sm={4} md={1.5}>
              <SummaryTile
                label="Staff logins"
                value={ds.staff_logins || 0}
                color={crmColors.info.main}
                icon={<LoginIcon sx={{ fontSize: 20 }} />}
                active={viewMode === "daily" && dailyActivity === "login"}
                showExplore
                onClick={() =>
                  selectDailyActivity(dailyActivity === "login" ? "all" : "login")
                }
                onExplore={() =>
                  navigate(`/sop/explore-activity?activity=login&range=${dailyRange}`)
                }
              />
            </Grid>
            <Grid item xs={6} sm={4} md={1.5}>
              <SummaryTile
                label="Fee receipts"
                value={ds.fee_receipts || 0}
                color={crmColors.energy.main}
                icon={<FeeIcon sx={{ fontSize: 20 }} />}
                active={viewMode === "daily" && dailyActivity === "fee"}
                showExplore
                onClick={() =>
                  selectDailyActivity(dailyActivity === "fee" ? "all" : "fee")
                }
                onExplore={() =>
                  navigate(`/sop/explore-activity?activity=fee&range=${dailyRange}`)
                }
              />
            </Grid>
            <Grid item xs={6} sm={4} md={1.5}>
              <SummaryTile
                label="Student att."
                value={ds.attendance_sections || 0}
                color={crmColors.warning.main}
                icon={<AttendanceIcon sx={{ fontSize: 20 }} />}
                active={viewMode === "daily" && dailyActivity === "attendance"}
                showExplore
                onClick={() =>
                  selectDailyActivity(
                    dailyActivity === "attendance" ? "all" : "attendance"
                  )
                }
                onExplore={() =>
                  navigate(
                    `/sop/explore-activity?activity=attendance&range=${dailyRange}`
                  )
                }
              />
            </Grid>
            <Grid item xs={6} sm={4} md={1.5}>
              <SummaryTile
                label="Staff att."
                value={ds.staff_attendance || 0}
                color={crmColors.secondary.main}
                icon={<StaffAttIcon sx={{ fontSize: 20 }} />}
                active={viewMode === "daily" && dailyActivity === "staff_attendance"}
                showExplore
                onClick={() =>
                  selectDailyActivity(
                    dailyActivity === "staff_attendance" ? "all" : "staff_attendance"
                  )
                }
                onExplore={() =>
                  navigate(
                    `/sop/explore-activity?activity=staff_attendance&range=${dailyRange}`
                  )
                }
              />
            </Grid>
            <Grid item xs={6} sm={4} md={1.5}>
              <SummaryTile
                label="Homework"
                value={ds.homework || 0}
                color={crmColors.primary.dark || crmColors.primary.main}
                icon={<HomeworkIcon sx={{ fontSize: 20 }} />}
                active={viewMode === "daily" && dailyActivity === "homework"}
                showExplore
                onClick={() =>
                  selectDailyActivity(dailyActivity === "homework" ? "all" : "homework")
                }
                onExplore={() =>
                  navigate(
                    `/sop/explore-activity?activity=homework&range=${dailyRange}`
                  )
                }
              />
            </Grid>
            <Grid item xs={6} sm={4} md={1.5}>
              <SummaryTile
                label="Expense"
                value={ds.expenses || 0}
                color={crmColors.error.main}
                icon={<ExpenseIcon sx={{ fontSize: 20 }} />}
                active={viewMode === "daily" && dailyActivity === "expense"}
                showExplore
                onClick={() =>
                  selectDailyActivity(dailyActivity === "expense" ? "all" : "expense")
                }
                onExplore={() =>
                  navigate(
                    `/sop/explore-activity?activity=expense&range=${dailyRange}`
                  )
                }
              />
            </Grid>
          </Grid>
        )}

        <Box
          sx={{
            p: 1.5,
            borderRadius: "14px",
            border: `1px solid ${crmColors.border.default}`,
            background: `linear-gradient(180deg, ${alpha(crmColors.primary.main, 0.04)} 0%, ${crmColors.background.paper} 100%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            alignItems={{ md: "center" }}
          >
            <TextField
              size="small"
              placeholder="Search school, code, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...fieldSx, minWidth: { md: 280 }, flex: 1 }}
            />
            {viewMode === "health" ? (
              <>
                <TextField
                  select
                  size="small"
                  label="Band"
                  value={band}
                  onChange={(e) => setBand(e.target.value)}
                  sx={{ ...fieldSx, minWidth: 140 }}
                >
                  {BANDS.map((b) => (
                    <MenuItem key={b} value={b}>
                      {b === "ALL" ? "All bands" : b}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Stage"
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                  sx={{ ...fieldSx, minWidth: 180 }}
                >
                  <MenuItem value="ALL">All stages</MenuItem>
                  {stageOptions.map((s) => (
                    <MenuItem key={s.stage} value={s.stage}>
                      {s.stage}. {s.label}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            ) : (
              <>
                <TextField
                  select
                  size="small"
                  label="Period"
                  value={dailyRange}
                  onChange={(e) => {
                    setDailyPage(1);
                    setDailyRange(e.target.value);
                  }}
                  sx={{ ...fieldSx, minWidth: 150 }}
                >
                  {DAILY_RANGES.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </TextField>
                {viewMode === "daily" ? (
                  <TextField
                    select
                    size="small"
                    label="Activity"
                    value={dailyActivity}
                    onChange={(e) => {
                      setDailyPage(1);
                      setDailyActivity(e.target.value);
                    }}
                    sx={{ ...fieldSx, minWidth: 170 }}
                  >
                    {DAILY_ACTIVITIES.map((a) => (
                      <MenuItem key={a.value} value={a.value}>
                        {a.label}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null}
                <TextField
                  select
                  size="small"
                  label="Show"
                  value={dailyPageSize}
                  onChange={(e) => {
                    setDailyPage(1);
                    setDailyPageSize(Number(e.target.value));
                  }}
                  sx={{ ...fieldSx, minWidth: 120 }}
                >
                  {DAILY_PAGE_SIZES.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} schools
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}
            <Chip
              size="small"
              label={
                isActivityView
                  ? `${pageTotal} with activity`
                  : `${pageTotal} schools`
              }
              sx={{
                height: 32,
                fontWeight: 800,
                bgcolor: alpha(crmColors.primary.main, 0.1),
                color: crmColors.primary.main,
              }}
            />
          </Stack>
        </Box>

        {isActivityView ? (
          pageLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 7 }}>
              <CircularProgress size={28} sx={{ color: crmColors.primary.main }} />
            </Box>
          ) : dailyRows.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 7,
                px: 2,
                borderRadius: "16px",
                border: `1px dashed ${crmColors.border.default}`,
                bgcolor: alpha(crmColors.primary.main, 0.02),
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>No activity in this period</Typography>
              <Typography variant="caption" color="text.secondary">
                Schools with staff login, fee collection, or attendance in{" "}
                {dailyRangeLabel.toLowerCase()} will appear here
              </Typography>
            </Box>
          ) : (
            <>
            <Stack spacing={1.15}>
              {dailyRows.map((row, index) => {
                const color = BAND_COLOR[row.health_band] || BAND_COLOR.Unknown;
                const serial = (dailyPage - 1) * dailyPageSize + index + 1;
                return (
                  <Box
                    key={row.school_code}
                    onClick={() => openSopDetails(row)}
                    sx={{
                      display: "flex",
                      borderRadius: "14px",
                      overflow: "hidden",
                      cursor: "pointer",
                      border: `1px solid ${crmColors.border.default}`,
                      background: `linear-gradient(120deg, ${crmColors.background.paper} 0%, ${alpha(
                        crmColors.primary.main,
                        0.04
                      )} 100%)`,
                      "&:hover": {
                        borderColor: alpha(crmColors.primary.main, 0.32),
                        boxShadow: `0 8px 22px ${alpha(crmColors.primary.main, 0.1)}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 5,
                        alignSelf: "stretch",
                        background: color,
                        flexShrink: 0,
                      }}
                    />
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1.5,
                        px: 1.75,
                        py: 1.35,
                      }}
                    >
                      <Typography
                        sx={{
                          width: 36,
                          flexShrink: 0,
                          fontWeight: 800,
                          fontSize: 13,
                          color: crmColors.text.secondary,
                          textAlign: "center",
                        }}
                      >
                        {serial}
                      </Typography>
                      <Box sx={{ flex: "1 1 180px", minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 14.5 }}>
                          {row.school_name || "—"}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: crmColors.text.secondary }}>
                          {row.school_code}
                          {row.admin_phone ? ` · ${row.admin_phone}` : ""}
                        </Typography>
                        <Box
                          sx={{
                            mt: 0.45,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 0.85,
                            py: 0.2,
                            borderRadius: "6px",
                            bgcolor: alpha(crmColors.logo.brand, 0.18),
                            border: `1px solid ${alpha(crmColors.logo.brand, 0.35)}`,
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: crmColors.logo.dark,
                            }}
                          >
                            Last activity ·
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 11.5,
                              fontWeight: 800,
                              color: crmColors.success.main,
                              letterSpacing: "0.01em",
                            }}
                          >
                            {formatTime(row.last_activity_at)}
                          </Typography>
                        </Box>
                      </Box>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          icon={<LoginIcon sx={{ fontSize: "14px !important" }} />}
                          label={`${row.staff_logins || 0} logins`}
                          sx={{
                            height: 24,
                            fontWeight: 700,
                            bgcolor: row.has_login
                              ? alpha(crmColors.info.main, 0.12)
                              : alpha(crmColors.secondary.main, 0.08),
                            color: row.has_login
                              ? crmColors.info.dark || crmColors.info.main
                              : crmColors.text.secondary,
                          }}
                        />
                        <Chip
                          size="small"
                          icon={<FeeIcon sx={{ fontSize: "14px !important" }} />}
                          label={`${row.fee_receipts || 0} fees`}
                          sx={{
                            height: 24,
                            fontWeight: 700,
                            bgcolor: row.has_fee
                              ? alpha(crmColors.success.main, 0.12)
                              : alpha(crmColors.secondary.main, 0.08),
                            color: row.has_fee
                              ? crmColors.success.dark
                              : crmColors.text.secondary,
                          }}
                        />
                        <Chip
                          size="small"
                          icon={<AttendanceIcon sx={{ fontSize: "14px !important" }} />}
                          label={`${row.attendance_sections || 0} stu att.`}
                          sx={{
                            height: 24,
                            fontWeight: 700,
                            bgcolor: row.has_attendance
                              ? alpha(crmColors.energy.main, 0.12)
                              : alpha(crmColors.secondary.main, 0.08),
                            color: row.has_attendance
                              ? crmColors.energy.dark
                              : crmColors.text.secondary,
                          }}
                        />
                        <Chip
                          size="small"
                          icon={<StaffAttIcon sx={{ fontSize: "14px !important" }} />}
                          label={`${row.staff_attendance || 0} staff att.`}
                          sx={{
                            height: 24,
                            fontWeight: 700,
                            bgcolor: row.has_staff_attendance
                              ? alpha(crmColors.secondary.main, 0.14)
                              : alpha(crmColors.secondary.main, 0.08),
                            color: row.has_staff_attendance
                              ? crmColors.secondary.dark || crmColors.secondary.main
                              : crmColors.text.secondary,
                          }}
                        />
                        <Chip
                          size="small"
                          icon={<HomeworkIcon sx={{ fontSize: "14px !important" }} />}
                          label={`${row.homework || 0} HW`}
                          sx={{
                            height: 24,
                            fontWeight: 700,
                            bgcolor: row.has_homework
                              ? alpha(crmColors.primary.main, 0.12)
                              : alpha(crmColors.secondary.main, 0.08),
                            color: row.has_homework
                              ? crmColors.primary.main
                              : crmColors.text.secondary,
                          }}
                        />
                        <Chip
                          size="small"
                          icon={<ExpenseIcon sx={{ fontSize: "14px !important" }} />}
                          label={`${row.expenses || 0} exp.`}
                          sx={{
                            height: 24,
                            fontWeight: 700,
                            bgcolor: row.has_expense
                              ? alpha(crmColors.error.main, 0.12)
                              : alpha(crmColors.secondary.main, 0.08),
                            color: row.has_expense
                              ? crmColors.error.dark || crmColors.error.main
                              : crmColors.text.secondary,
                          }}
                        />
                        {(row.notices || 0) > 0 ? (
                          <Chip
                            size="small"
                            icon={<NoticeIcon sx={{ fontSize: "14px !important" }} />}
                            label={`${row.notices} notices`}
                            sx={{
                              height: 24,
                              fontWeight: 700,
                              bgcolor: alpha(crmColors.primary.main, 0.1),
                              color: crmColors.primary.main,
                            }}
                          />
                        ) : null}
                      </Stack>
                      <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        disabled={!row.school_code}
                        startIcon={<DetailsIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => goExploreSchool(e, row.school_code)}
                        sx={{ ...exploreBtnSx, ml: { xs: 0, md: "auto" }, flexShrink: 0 }}
                      >
                        Explore
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 1.5, px: 0.5 }}
            >
              <Typography sx={{ fontSize: 12.5, color: crmColors.text.secondary, fontWeight: 600 }}>
                Showing {dailyFrom}–{dailyTo} of {dailyTotal}
              </Typography>
              <Pagination
                color="primary"
                shape="rounded"
                size="small"
                count={dailyPageCount}
                page={dailyPage}
                onChange={(_, p) => setDailyPage(p)}
                siblingCount={1}
                boundaryCount={1}
                disabled={pageLoading || dailyPageCount <= 1}
                sx={{
                  "& .MuiPaginationItem-root": { fontWeight: 700 },
                }}
              />
            </Stack>
            </>
          )
        ) : pageLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 7 }}>
            <CircularProgress size={28} sx={{ color: crmColors.primary.main }} />
          </Box>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 7,
              px: 2,
              borderRadius: "16px",
              border: `1px dashed ${crmColors.border.default}`,
              bgcolor: alpha(crmColors.primary.main, 0.02),
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                mx: "auto",
                mb: 1.25,
                borderRadius: "14px",
                display: "grid",
                placeItems: "center",
                background: `linear-gradient(145deg, ${crmColors.primary.main}, ${crmColors.hero.start})`,
                color: "#fff",
                boxShadow: `0 8px 18px ${alpha(crmColors.primary.main, 0.28)}`,
              }}
            >
              <SopIcon sx={{ fontSize: 26 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: crmColors.text.primary }}>
              No SOP scores yet
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              Refresh scores to compute setup and usability from live school data
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onRefreshBatch}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 800,
                bgcolor: crmColors.energy.main,
                "&:hover": { bgcolor: crmColors.energy.dark },
              }}
            >
              Refresh scores
            </Button>
          </Box>
        ) : (
          <Stack spacing={1.15}>
            {rows.map((row, index) => {
              const color = BAND_COLOR[row.health_band] || BAND_COLOR.Unknown;
              return (
                <Box
                  key={row.school_code}
                  onClick={() => openSopDetails(row)}
                  sx={{
                    display: "flex",
                    borderRadius: "14px",
                    overflow: "hidden",
                    cursor: "pointer",
                    border: `1px solid ${crmColors.border.default}`,
                    background: `linear-gradient(120deg, ${crmColors.background.paper} 0%, ${alpha(
                      color,
                      0.05
                    )} 100%)`,
                    transition: "transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      borderColor: alpha(crmColors.primary.main, 0.32),
                      boxShadow: `0 8px 22px ${alpha(crmColors.primary.main, 0.1)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 4, sm: 5 },
                      alignSelf: "stretch",
                      background: color,
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: { xs: 1.25, md: 2 },
                      px: { xs: 1.35, sm: 1.75 },
                      py: 1.35,
                    }}
                  >
                    <Typography
                      sx={{
                        width: 28,
                        flexShrink: 0,
                        fontWeight: 800,
                        fontSize: 13,
                        color: crmColors.text.secondary,
                        textAlign: "center",
                      }}
                    >
                      {index + 1}
                    </Typography>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "12px",
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: alpha(color, 0.14),
                        color,
                      }}
                    >
                      <SchoolIcon sx={{ fontSize: 20 }} />
                    </Box>

                    <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: 14.5,
                            color: crmColors.text.primary,
                            lineHeight: 1.25,
                          }}
                        >
                          {row.school_name || "—"}
                        </Typography>
                        <Chip
                          size="small"
                          label={row.health_band || "Unknown"}
                          sx={{
                            height: 20,
                            fontWeight: 800,
                            fontSize: 10.5,
                            color: "#fff",
                            bgcolor: color,
                            "& .MuiChip-label": { px: 0.85 },
                          }}
                        />
                      </Stack>
                      <Typography sx={{ mt: 0.3, fontSize: 12, color: crmColors.text.secondary }}>
                        {row.school_code}
                        {row.admin_phone ? ` · ${row.admin_phone}` : ""}
                      </Typography>
                      <Typography sx={{ mt: 0.35, fontSize: 12.5, fontWeight: 700, color: crmColors.text.primary }}>
                        Stage {row.sop_stage}. {row.sop_stage_label}
                        <Typography
                          component="span"
                          sx={{ ml: 0.75, fontSize: 11.5, fontWeight: 600, color: crmColors.text.secondary }}
                        >
                          {row.subscription_status || "—"}
                        </Typography>
                      </Typography>
                    </Box>

                    <Stack
                      spacing={0.65}
                      sx={{ flex: "1 1 180px", minWidth: 140, maxWidth: { md: 220 } }}
                    >
                      <ScoreBar
                        label="Setup"
                        value={row.setup_score}
                        max={40}
                        color={crmColors.primary.main}
                      />
                      <ScoreBar
                        label="Usage"
                        value={row.usage_score}
                        max={40}
                        color={crmColors.success.main}
                      />
                      <ScoreBar
                        label="Parent"
                        value={row.parent_score}
                        max={20}
                        color={crmColors.energy.main}
                      />
                      <Typography sx={{ fontSize: 11.5, fontWeight: 800 }}>
                        Total {row.usability_score}/100
                      </Typography>
                    </Stack>

                    <Box sx={{ flex: "1 1 160px", minWidth: 140 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: crmColors.text.secondary }}>
                        Last fee · {formatDate(row.last_fee_at)}
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: crmColors.text.secondary, mt: 0.35 }}>
                        Attendance · {formatDate(row.last_attendance_at)}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.65,
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: crmColors.text.primary,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {row.next_action || "No next action"}
                      </Typography>
                    </Box>

                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      disabled={!row.school_code}
                      startIcon={<DetailsIcon sx={{ fontSize: 14 }} />}
                      onClick={(e) => goExploreSchool(e, row.school_code)}
                      sx={{ ...exploreBtnSx, ml: { xs: 0, md: "auto" }, flexShrink: 0 }}
                    >
                      Explore
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>

    </PageContentLayout>
  );
};

export default SOP;
